import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Avatar,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useParams } from "react-router-dom";
import { ImageService } from "../../services/images/images";
import { CartService } from "../../services/functions/cartService";
import { CartServiceRefundOrderResponseType } from "../../models/CartServiceType";
import { MetricsNames } from "../../services/firebase/metricsService";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { logEvent } from "../../firebase";

const requestRefund = async (orderId: string, message: string, imageUrl: string): Promise<CartServiceRefundOrderResponseType> => {
  const refundResponse = await CartService.requestOrderRefund({
    images: imageUrl != "" ? [imageUrl] : [],
    message: message,
    orderHistoryId: orderId
  })
  return refundResponse;
};

const RefundRequestPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!orderId || !message) return;

    setIsSubmitting(true);
    try {
      var imageUrl: string = "";
      if (selectedFile) {
        imageUrl = (await ImageService.uploadImagesToStorage([selectedFile]))[0];;
      }
      const refundResponse: CartServiceRefundOrderResponseType = await requestRefund(orderId, message, imageUrl);
      if (refundResponse.success) {

        logEvent(MetricsNames.REFUND_REQUESTED);
        navigate("/order/history");
      } else {
        console.log(refundResponse)
        // TODO: ADD Some sort of error management
      }
    } catch (error) {
      console.error("Error submitting refund request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "start",
      flexDirection: "column",
      padding: 2,
    }}
      className={'patlite'}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 600,
          mx: 'auto',
          pb: 4,
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          textAlign="center"
          sx={{
            fontSize: {
              xs: '1.5rem',
              sm: '2rem',
              md: '2.5rem',
            },
            wordBreak: 'break-word',
          }}
        >
          Request Refund for Order{' '}
          <br />
          <Box component="span" sx={{ fontSize: '0.8em' }}>
            {orderId!.replace(/(.{4})/g, '$1 ').trim()}
          </Box>
        </Typography>
      </Box>

      <form
        onSubmit={handleSubmit}
        style={{
          padding: 24,
          backgroundColor: "white",
          borderRadius: 16,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          boxSizing: "border-box",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
          flexDirection: "column",
          alignSelf: "center",
          width: "100%",
          maxWidth: 600,
          minHeight: 300,
        }}
      >
        <TextField
          label="Refund Reason"
          value={message}

          onChange={(e) => setMessage(e.target.value)}
          multiline
          rows={4}
          fullWidth
          required
          sx={{ mb: 1 }}
        />

        <input
          accept="image/*"
          type="file"
          id="upload-image"
          hidden
          onChange={handleFileChange}
        />
        <label htmlFor="upload-image">
          <Button
            variant="contained"
            component="span"
            startIcon={<CloudUploadIcon />}
            sx={{
              mb: 1,
              borderRadius: "24px",
              backgroundColor: '#162C1F',
              '&:hover': {
                backgroundColor: '#0f1f15',
              },
              width: "100%",
              paddingLeft: 8,
              paddingRight: 8,
              minWidth: 300,
            }}
          >
            Upload Proof
          </Button>
        </label>

        <Box sx={{ mt: 1, mb: 2 }}>
          <Button
            type="submit"
            variant="contained"
            sx={{
              mb: 0,
              minWidth: 300,
              borderRadius: "24px",
              backgroundColor: '#162C1F',
              '&:hover': {
                backgroundColor: '#0f1f15',
              },
              width: "100%",
              paddingLeft: 8,
              paddingRight: 8,
              maxWidth: 300,
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} />
            ) : (
              "Submit Request"
            )}
          </Button>
        </Box>
        {selectedFile && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-evenly",
              borderRadius: 2,
              border: '1px #e0e0e0',
              backgroundColor: '#f0f0f0',
              minWidth: 300,
              maxWidth: 300,
            }}
          >
            <Avatar sx={{ paddingLeft: 2, display: "flex" }} variant="rounded" src={URL.createObjectURL(selectedFile)} />
            <Typography sx={{ padding: 2 }} variant="caption">{selectedFile.name}</Typography>
            <IconButton onClick={() => setSelectedFile(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </form>
    </Box>
  );
};

export default RefundRequestPage;